<template>
  <div>
    <v-card>
      <v-card-title>Leave Summary Report</v-card-title>
      <v-row class="px-4">
        <v-col>
          <v-row class="px-4">
            <v-col>
              <v-autocomplete
                v-model="leaveType"
                :loading="dropdownLoading"
                label="Leave Type"
                :items="leaveTypes"
                :error-messages="errors.collect('leaveType')"
                data-vv-name="leaveType"
                data-vv-as="leave type"
                chips
                deletable-chips
                hide-selected
                multiple
                item-text="name"
                item-value="id"
                small-chips
                clearable
              />
            </v-col>

            <v-col>
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                :disabled="years.length < 1"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="startDateDisplay"
                    v-validate="'required'"
                    label="Start Date"
                    prepend-icon="mdi-calendar"
                    :error-messages="errors.collect('startDateDisplay')"
                    data-vv-name="startDateDisplay"  
                    data-vv-as="start date"    
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="startDate"
                  :min="minStartDate"
                  :max="maxStartDate"
                  @input="startDateChange"
                />
              </v-menu>
            </v-col>

            <v-col>
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                :disabled="years.length < 1"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="endDateDisplay"
                    v-validate="'required'"
                    label="End Date"
                    prepend-icon="mdi-calendar"
                    :error-messages="errors.collect('endDateDisplay')"
                    data-vv-name="endDateDisplay"  
                    data-vv-as="end date"  
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="endDate"
                  :min="startDate"
                  :max="maxEndDate"
                  @input="endDateMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>

          <v-row class="px-4">
            <v-col>
              <v-autocomplete
                v-model="department"
                :loading="dropdownLoading"
                label="Departments"
                :items="departments"
                chips
                deletable-chips
                hide-selected
                multiple
                item-text="name"
                item-value="id"
                small-chips
                clearable
              />
            </v-col>

            <v-col>
              <v-autocomplete
                v-model="state"
                :loading="dropdownLoading"
                label="States"
                :items="states"
                chips
                deletable-chips
                hide-selected
                multiple
                item-text="name"
                item-value="id"
                small-chips
                clearable
              />
            </v-col>

            <v-col>
              <v-autocomplete
                v-model="position"
                label="Positions"
                :items="positions"
                multiple
                item-text="label"
                chips
                deletable-chips
                hide-selected
                :loading="dropdownLoading"
                small-chips
                clearable 
                item-value="id"
              />
            </v-col>
          </v-row>

          <v-row class="px-4">
            <v-col
              cols="4"
            >
              <v-autocomplete
                v-model="status"
                v-validate="'required'"
                label="Status"
                item-text="name"
                item-value="id"
                :items="leaveStatus"
                clearable
                multiple
                hide-selected 
                chips
                small-chips
                deletable-chips                
                :error-messages="errors.collect('status')"
                data-vv-name="status"
                data-vv-as="status"
              />
            </v-col>
          </v-row>
        </v-col>

        <v-col
          cols="2"
          class="ma-auto"
        >
          <v-row
            v-if="loading" 
            class="text-center"
          >
            <v-col cols="12">
              <div>
                <span class="font-weight-bold">Downloading</span>
              </div>
              <v-progress-circular
                :rotate="360"
                :size="50"
                :width="5"
                :value="progressbar"
                color="teal"
              >
                {{ progressbar }}
              </v-progress-circular>
            </v-col>
          </v-row>
      
          <v-row class="text-center">
            <v-col cols="12">
              <v-btn
                class="mx-3"
                color="primary"
                :loading="loading"
                :disabled="years.length < 1"
                @click="generateLeaveReports"
              >
                generate
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    data () {
      return {
        loading: false,
        department: [],
        departments: [],
        leaveType: [],
        leaveTypes: [],
        state: [],
        states: [],
        minYear: null,
        maxYear: null,
        years: [],
        startDateMenu: false,
        startDate: this.$moment().startOf('month').format('YYYY-MM-DD'),
        endDateMenu: false,
        endDate: this.$moment().format('YYYY-MM-DD'),            
        dropdownLoading: false,
        progressbar: 0,
        positions: [],
        position: [],
        leaveStatus: [],
        status: [],     
      }
    },
    computed: {
      startDateDisplay () {
        return this.startDate ? this.$moment(this.startDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      endDateDisplay () {
        return this.endDate ? this.$moment(this.endDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      maxEndDate () {
        return this.startDate ? this.$moment(this.startDate).endOf('year').format('YYYY-MM-DD') : this.$moment().endOf('year').format('YYYY-MM-DD')
      },   
      minStartDate () {
        return this.$moment([this.minYear]).format('YYYY-MM-DD')
      },
      maxStartDate () {
        return this.$moment([this.maxYear]).endOf('year').format('YYYY-MM-DD')
      },          
    },      
    created () {
      this.dropdownLoading = true

      Promise.all([
        this.$api.hrisDepartments().list(['name'], false),
        this.$api.hrisLeaveTypes().list(),
        this.$api.states().list(),
        this.$api.leaveReports().approvedLeaveYears(),
        this.$api.positions().list(),
        this.$api.approvalStatus().list(),
      ])
        .then(([departments, leaveTypes, states, leaveReportYears, position, status]) => {
          this.departments = departments.data        
          this.leaveTypes = leaveTypes.data        
          this.states = states.data   
          this.years = leaveReportYears.data     
          this.minYear = Math.min(...this.years)  
          this.maxYear = Math.max(...this.years)  
          this.positions = position.data.map(pos => ({
            id: pos.id,
            label: `${pos.name} (${pos.job_grade.job_group.name})`,
          }))
          let statusType = status.data.filter(item => {
            return item.id === 100 || item.id === 200 || item.id === 500 || item.id === 501
          })
          this.leaveStatus = _.orderBy(statusType, ['id'], ['asc'])
          
          this.dropdownLoading = false
        })
    },
    methods: {
      startDateChange () {
        this.startDateMenu = false
        this.endDate = this.$moment(this.startDate).endOf('month').format('YYYY-MM-DD')
      },          
      generateLeaveReports () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.loading = true

            this.$api.leaveReports().generateLeaveReport(this.prepareFormData(), {
              onDownloadProgress: (progressEvent) => {
                this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
              },
            })
              .then(res => {
                let year = this.$moment(this.startDate).format('YYYY')

                let blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
                let link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                let statusName = []
                this.leaveStatus.find(status => {
                  this.status.map(s => {
                    if (status.id === s) {
                      statusName.push(status.name)
                    }
                  })
                })
                statusName.length > 0 ? link.setAttribute('download', statusName + ' Leave Summary Report-' + year + '.xlsx') : link.setAttribute('download', 'Leave Summary Report-' + year + '.xlsx')
                link.click()
                this.loading = false
                this.progressbar = 0

                this.$snack({ message: 'Report downloaded successfully' })
              }).catch(() => {
                this.loading = false
                this.$snack({ color: 'error', message: 'Error! Please contact support' })
              })
          })
      },

      prepareFormData () {
        return {
          states: this.state,
          departments: this.department,
          leaveTypes: this.leaveType,
          start_date: this.startDate,
          end_date: this.endDate,
          positions: this.position,
          status: this.status,
        }
      },
    },
  }
</script>
